/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  BaseOptionList,
  OnePassOptions,
  OnePassValues,
  OptionItemBase,
  OptionType
} from "@tivocorp/data-domain-lib/client-code/onepass";

import { Locale } from "../locale";
import { Log } from "../logging/Log";
import { TOverlayOptions } from "./TOverlayOptions";

/**
 * Divider style hint emitted for every option row.
 */
const SEPARATOR_TYPE_OPTIONS: string = "options";

/**
 * Canonical OnePass option-key order. Governs both interdependency-rule
 * precedence and render order (deterministic-ordering requirement). These
 * are the real {@link OnePassOptions}/{@link OnePassValues} property keys —
 * the five rows of the Series Recording Options overlay (Episodes, Channel,
 * Keep at Most, Start Recording, Stop Recording) map onto them in this order.
 */
const ONE_PASS_OPTIONS_ORDER: readonly string[] = [
  "include",
  "recordChannel",
  "keepAtMost",
  "startRecordingEarly",
  "stopRecordingLate"
];

/**
 * An option group as seen by the engine: the shared metadata plus the
 * group's selectable items. Every typed OnePass option-list from the data
 * layer (IncludeOptionList, VideoQualityOptionList, …) is assignable to it.
 */
type TOnePassOptionList = BaseOptionList & {
  optionList?: OptionItemBase[];
};

/**
 * Computes the interdependencies between the user-selectable options of the
 * OnePass (series-recording) options overlay.
 *
 * The interdependency rules (`disables` / `forcedSelection`) are entirely
 * data-driven — they travel inside the backend payload and are only
 * interpreted here, never hardcoded. Domain shapes come from the data layer
 * (`@tivocorp/data-domain-lib/client-code/onepass`); the engine only adds
 * the render model {@link TOverlayOptions}.
 *
 * State is static (shared), so the engine supports a single active overlay
 * at a time: opening a new overlay via {@link setOnePassOptions} reseeds the
 * shared maps. Consumers must {@link clearOnePassOptions} on close.
 */
export class DynamicOptionsEngine {
  private static readonly TAG: string = "DynamicOptionsEngine";

  /**
   * Visible option groups keyed by option key.
   * @type {Map<string, TOnePassOptionList>}
   */
  private static mOnePassOptionsMap?: Map<string, TOnePassOptionList>;

  /**
   * Currently selected raw values keyed by option key (backend submission
   * shape, seeded with defaults).
   * @type {OnePassValues}
   */
  private static mOnePassOptionsWithValues?: OnePassValues;

  /**
   * Reverse lookup: optionListType → option key. Contains invisible groups
   * too, so rules and value mapping can reference them.
   * @type {Map<OptionType, string>}
   */
  private static mOptionListTypeMap?: Map<OptionType, string>;

  /**
   * Canonical option-key order used for both passes.
   * @type {ReadonlyArray<string>}
   */
  private readonly mOnePassOptionsOrder: readonly string[] =
    ONE_PASS_OPTIONS_ORDER;

  /**
   * Recursive deep-equality check: sorted-key structural comparison for
   * objects, strict `===` for primitives. Needed because value fields may
   * be objects (e.g. a channel descriptor). Null/undefined are handled
   * before key enumeration so the comparison never throws.
   */
  private compareValues(firstValue: unknown, secondValue: unknown): boolean {
    if (
      typeof firstValue === "object" &&
      typeof secondValue === "object" &&
      firstValue !== null &&
      secondValue !== null
    ) {
      const firstRecord = firstValue as Record<string, unknown>;
      const secondRecord = secondValue as Record<string, unknown>;
      const firstValueKeys = Object.keys(firstRecord).sort(
        (first: string, second: string): number => first.localeCompare(second)
      );
      const secondValueKeys = Object.keys(secondRecord).sort(
        (first: string, second: string): number => first.localeCompare(second)
      );
      if (firstValueKeys.toString() !== secondValueKeys.toString()) {
        return false;
      }
      return firstValueKeys.reduce(
        (accumulator: boolean, key: string): boolean =>
          accumulator &&
          this.compareValues(firstRecord[key], secondRecord[key]),
        true
      );
    }
    return firstValue === secondValue;
  }

  /**
   * Build a render row for one visible option group, applying the
   * accumulated disable/force rules.
   */
  private createOverlayOption(
    value: TOnePassOptionList,
    disabledOptions: Set<OptionType>,
    forcedOptions: Map<OptionType, string>
  ): TOverlayOptions {
    const optionListType = value.optionListType;
    const option: TOverlayOptions = {
      text: Locale.getString(
        `series_recording_overlay_option_${this.optionToLocaleKey(optionListType)}`
      ),
      horizontalSelectionoptions: this.optionLabels(value),
      hrBottom: value.insertDividerAfter === true,
      separatorType: SEPARATOR_TYPE_OPTIONS,
      value: {
        optionListType
      }
    };
    if (optionListType !== undefined && disabledOptions.has(optionListType)) {
      option.disabled = true;
    }
    if (optionListType !== undefined && forcedOptions.has(optionListType)) {
      const forcedLabel = forcedOptions.get(optionListType);
      option.initialHorizontalIndex = this.getOptionIndexFromLabel(
        value,
        forcedLabel
      );
    }
    return option;
  }

  /**
   * Gather the disable/force rules attached to the item currently selected
   * in `options`, mutating the shared accumulators.
   */
  private filterForcedAndDisabledOptions(
    options: TOnePassOptionList,
    forcedOptions: Map<OptionType, string>,
    disabledOptions: Set<OptionType>,
    selectedLabel: string
  ): void {
    const optionInfo = this.findItemByLabel(options, selectedLabel);
    if (optionInfo === undefined) {
      return;
    }
    if (optionInfo.forcedSelection !== undefined) {
      optionInfo.forcedSelection.forEach((forced): void => {
        const forcedType = forced.optionType;
        const forcedItem = forced.item;
        if (
          forcedType !== undefined &&
          forcedItem !== undefined &&
          forcedItem.label !== undefined
        ) {
          forcedOptions.set(forcedType, forcedItem.label);
        }
      });
    }
    if (optionInfo.disables !== undefined) {
      optionInfo.disables.forEach((disabled): void => {
        if (disabled.optionListType !== undefined) {
          disabledOptions.add(disabled.optionListType);
        }
      });
    }
  }

  /**
   * Take the single non-meta field of an item as its submitted raw value,
   * without assuming the field's name. Returns undefined when the item
   * carries no value field.
   */
  private extractItemValue(item: OptionItemBase): unknown {
    const record = item as unknown as Record<string, unknown>;
    const valueFieldName = Object.keys(record).find(
      (fieldName: string): boolean =>
        // The value field is the single non-meta key; "label", "disables" and
        // "forcedSelection" are the known metadata keys carried by every item.
        fieldName !== "label" &&
        fieldName !== "disables" &&
        fieldName !== "forcedSelection"
    );
    return valueFieldName === undefined ? undefined : record[valueFieldName];
  }

  /**
   * Locate an item within a group by its label identity.
   */
  private findItemByLabel(
    options: TOnePassOptionList,
    label: string
  ): OptionItemBase | undefined {
    if (options.optionList === undefined) {
      return undefined;
    }
    return options.optionList.find(
      (item: OptionItemBase): boolean => item.label === label
    );
  }

  /**
   * Reverse-map a stored raw value to the label of the item that carries
   * it; the value field compared depends on the group's optionListType. On
   * miss, log a diagnostic and fall back to the first item's label — never
   * throw.
   */
  private getLabelFromOptionValue(
    options: TOnePassOptionList,
    value: unknown
  ): string {
    const valueField = this.valueFieldForType(options.optionListType);
    let optionInfo: OptionItemBase | undefined;
    if (valueField !== undefined && options.optionList !== undefined) {
      optionInfo = options.optionList.find((item: OptionItemBase): boolean =>
        this.compareValues(
          (item as unknown as Record<string, unknown>)[valueField],
          value
        )
      );
    }
    if (optionInfo === undefined) {
      Log.e(
        DynamicOptionsEngine.TAG,
        `getLabelFromOptionValue() value "${JSON.stringify(
          value
        )}" not found in option list ${JSON.stringify(options)}.`
      );
      optionInfo =
        options.optionList === undefined ? undefined : options.optionList[0];
    }
    return optionInfo === undefined ? "" : optionInfo.label || "";
  }

  /**
   * Resolve a label to its index within a group; on miss, log a diagnostic
   * and fall back to index 0 — never throw.
   */
  private getOptionIndexFromLabel(
    options: TOnePassOptionList,
    label?: string
  ): number {
    if (options.optionList === undefined) {
      return 0;
    }
    const index = options.optionList.findIndex(
      (item: OptionItemBase): boolean => item.label === label
    );
    if (index < 0) {
      Log.e(
        DynamicOptionsEngine.TAG,
        `getOptionIndexFromLabel() label "${label}" not found in option list ${JSON.stringify(
          options
        )}.`
      );
      return 0;
    }
    return index;
  }

  /**
   * The selectable value labels of a group, in order.
   */
  private optionLabels(options: TOnePassOptionList): string[] {
    if (options.optionList === undefined) {
      return [];
    }
    return options.optionList.map(
      (item: OptionItemBase): string => item.label || ""
    );
  }

  /**
   * Derive the localized header key suffix from a group identity by
   * converting camelCase to lower snake_case (e.g. "videoQuality" →
   * "video_quality"), matching the locale-file key convention.
   */
  private optionToLocaleKey(optionListType?: OptionType): string {
    if (optionListType === undefined) {
      return "";
    }
    return optionListType.replaceAll(
      /[A-Z]/g,
      (letter: string): string => `_${letter.toLowerCase()}`
    );
  }

  /**
   * The name of the typed value field carried by a group's items, keyed by
   * the group's optionListType. This is the single place the per-group
   * value-field name is resolved; it is intentionally distinct from both
   * the option key and the optionListType.
   */
  private valueFieldForType(optionListType?: OptionType): string | undefined {
    switch (optionListType) {
      case OptionType.IncludeType:
        return "includeValue";
      case OptionType.Channel:
        return "recordChannel";
      case OptionType.KeepAtMost:
        return "keepAtMost";
      case OptionType.StartRecording:
        return "startRecording";
      case OptionType.StopRecording:
        return "stopRecording";
      default:
        return undefined;
    }
  }

  /**
   * Tear down all shared engine state.
   */
  public clearOnePassOptions(): void {
    delete DynamicOptionsEngine.mOnePassOptionsMap;
    delete DynamicOptionsEngine.mOptionListTypeMap;
    delete DynamicOptionsEngine.mOnePassOptionsWithValues;
  }

  /**
   * Recompute the render model after a user selection change. Pass 1 walks
   * the groups in canonical order and gathers all disable/force rules from
   * the selected items; pass 2 renders every visible group with those rules
   * applied (two passes are required because a later group can disable or
   * force an earlier one).
   * @param currentOptions - optionListType → currently selected label
   * @returns Render-ready rows in canonical order
   */
  public computeOnePassOptions(
    currentOptions: Map<OptionType, string>
  ): TOverlayOptions[] {
    const disabledOptions: Set<OptionType> = new Set<OptionType>();
    const forcedOptions: Map<OptionType, string> = new Map<
      OptionType,
      string
    >();
    const optionsMap = DynamicOptionsEngine.mOnePassOptionsMap;
    if (optionsMap === undefined) {
      return [];
    }
    this.mOnePassOptionsOrder.forEach((optionKey: string): void => {
      const option = optionsMap.get(optionKey);
      if (option === undefined || option.optionListType === undefined) {
        return;
      }
      const userSelectedLabel = currentOptions.get(option.optionListType);
      if (userSelectedLabel !== undefined) {
        this.filterForcedAndDisabledOptions(
          option,
          forcedOptions,
          disabledOptions,
          userSelectedLabel
        );
      }
    });

    const finalOptions: TOverlayOptions[] = [];
    this.mOnePassOptionsOrder.forEach((optionKey: string): void => {
      const value = optionsMap.get(optionKey);
      if (value !== undefined) {
        finalOptions.push(
          this.createOverlayOption(value, disabledOptions, forcedOptions)
        );
      }
    });
    return finalOptions;
  }

  /**
   * Build the initial render model from the stored defaults: each visible
   * group's default label is resolved by reverse-mapping its raw value, the
   * same disable/force accumulation runs, and every row gets an initial
   * index (the forced label's index when forced, otherwise the default
   * label's index).
   * @returns Render-ready rows in canonical order
   */
  public getInitialValuesOfOnePassOptions(): TOverlayOptions[] {
    const finalOptions: TOverlayOptions[] = [];
    const disabledOptions: Set<OptionType> = new Set<OptionType>();
    const forcedOptions: Map<OptionType, string> = new Map<
      OptionType,
      string
    >();
    const optionsMap = DynamicOptionsEngine.mOnePassOptionsMap;
    const optionsWithValues = DynamicOptionsEngine.mOnePassOptionsWithValues;
    if (optionsMap === undefined || optionsWithValues === undefined) {
      return [];
    }

    // Pass 1: resolve each group's default label and gather all rules
    // before rendering, so a later group can disable/force an earlier one.
    const defaultLabelByKey = new Map<string, string>();
    this.mOnePassOptionsOrder.forEach((optionKey: string): void => {
      const option = optionsMap.get(optionKey);
      if (option === undefined) {
        return;
      }
      const initialValue = (
        optionsWithValues as unknown as Record<string, unknown>
      )[optionKey];
      const label = this.getLabelFromOptionValue(option, initialValue);
      defaultLabelByKey.set(optionKey, label);
      this.filterForcedAndDisabledOptions(
        option,
        forcedOptions,
        disabledOptions,
        label
      );
    });

    // Pass 2: render every visible group with the accumulated rules.
    this.mOnePassOptionsOrder.forEach((optionKey: string): void => {
      const option = optionsMap.get(optionKey);
      if (option === undefined) {
        return;
      }
      const optionListType = option.optionListType;
      const overlayOption: TOverlayOptions = {
        text: Locale.getString(
          `series_recording_overlay_option_${this.optionToLocaleKey(optionListType)}`
        ),
        horizontalSelectionoptions: this.optionLabels(option),
        hrBottom: option.insertDividerAfter === true,
        separatorType: SEPARATOR_TYPE_OPTIONS,
        value: {
          optionListType
        }
      };
      if (optionListType !== undefined && disabledOptions.has(optionListType)) {
        overlayOption.disabled = true;
      }
      const forcedLabel =
        optionListType === undefined
          ? undefined
          : forcedOptions.get(optionListType);
      overlayOption.initialHorizontalIndex = this.getOptionIndexFromLabel(
        option,
        forcedLabel === undefined
          ? defaultLabelByKey.get(optionKey)
          : forcedLabel
      );
      finalOptions.push(overlayOption);
    });
    return finalOptions;
  }

  /**
   * Map the UI selection state back to the backend submission shape. Starts
   * from a shallow copy of the stored defaults so values of invisible /
   * non-nDVR groups are preserved, then overwrites each rendered group's
   * value with the raw value of the item at the chosen horizontal position.
   *
   * The submitted value is taken as the single non-meta field of the item,
   * without assuming its name — optionListType, option key and value-field
   * name are three distinct namespaces (e.g. "includeType" / "include" /
   * "includeValue").
   * @param options - Rows previously returned by compute/getInitial
   * @param horizontalPosition - Chosen horizontal index per row, parallel to options
   * @returns The backend-submittable values object
   */
  public getUserSelectedOnePassValues(
    options: TOverlayOptions[],
    horizontalPosition: number[]
  ): OnePassValues {
    const onePassValues: Record<string, unknown> = {
      ...DynamicOptionsEngine.mOnePassOptionsWithValues
    };
    const optionsMap = DynamicOptionsEngine.mOnePassOptionsMap;
    const typeMap = DynamicOptionsEngine.mOptionListTypeMap;
    if (optionsMap === undefined || typeMap === undefined) {
      return onePassValues as unknown as OnePassValues;
    }
    options.forEach((option: TOverlayOptions, index: number): void => {
      const labels = option.horizontalSelectionoptions;
      const optionListType = option.value.optionListType;
      if (labels === undefined || optionListType === undefined) {
        return;
      }
      const key = typeMap.get(optionListType);
      if (key === undefined) {
        return;
      }
      const onePassOptionValues = optionsMap.get(key);
      if (onePassOptionValues === undefined) {
        return;
      }
      const optionIndex = this.getOptionIndexFromLabel(
        onePassOptionValues,
        labels[horizontalPosition[index]]
      );
      const optionList = onePassOptionValues.optionList;
      const selectedItem =
        optionList === undefined ? undefined : optionList[optionIndex];
      if (selectedItem === undefined) {
        return;
      }
      // The submitted value is the item's single non-meta field; its name is
      // unknown to the engine (optionListType, key and value-field all differ).
      const itemValue = this.extractItemValue(selectedItem);
      if (itemValue !== undefined) {
        onePassValues[key] = itemValue;
      }
    });
    return onePassValues as unknown as OnePassValues;
  }

  /**
   * Seed the engine with a fresh backend payload. Shared maps are reset;
   * every group registers its optionListType → key reverse mapping, but
   * only groups with truthy visibility become renderable.
   * @param options - Option groups keyed by option key
   * @param optionsWithValues - Currently selected raw values (backend submission shape)
   */
  public setOnePassOptions(
    options: OnePassOptions,
    optionsWithValues: OnePassValues
  ): void {
    const optionsMap = new Map<string, TOnePassOptionList>();
    const typeMap = new Map<OptionType, string>();
    const optionsRecord = options as Record<
      string,
      TOnePassOptionList | undefined
    >;
    Object.keys(optionsRecord).forEach((key: string): void => {
      const group = optionsRecord[key];
      if (group === undefined || group.optionListType === undefined) {
        return;
      }
      typeMap.set(group.optionListType, key);
      if (group.visibility === true) {
        optionsMap.set(key, group);
      }
    });
    DynamicOptionsEngine.mOnePassOptionsMap = optionsMap;
    DynamicOptionsEngine.mOptionListTypeMap = typeMap;
    DynamicOptionsEngine.mOnePassOptionsWithValues = optionsWithValues;
  }
}
